import { AutoComplete, Form, Input, Modal, Select } from "antd";
import React, { useContext, useEffect, useState } from "react";
import { TransformedDataType } from "../../../api/numbers/getSipNumbersData";
import { getSipStatuses } from "../../../api/numbers/getSipStatuses";
import { getPools } from "../../../api/numbers/getPools";
import { putSipNumbers } from "../../../api/numbers/putSipNumbers";
import {
  getValueFromLabel,
  getLabelsAsValues,
  SelectOptionsType,
  selectOptionsComparator,
  createRegex,
} from "../../../utils/select-helpers";
import DisplayFieldValues from "../../../components/DisplayFieldValues";
import HeaderContext from "../../../components/layout/header/header-context/HeaderContext";
import { sipStatusChangeLogic } from "./EditNumberStatusFormModalProcessor";

interface Props {
  isVisible: boolean;
  handleClose: (refreshData?: boolean) => void;
  sipNumberRecord?: TransformedDataType;
}

interface State {
  isModalVisible: boolean;
  sipStatusesOptions?: SelectOptionsType[];
  poolsOptions?: SelectOptionsType[];
  submitError?: string;
  selectedSipStatus: string;
  poolsRegex: RegExp;
}

const EditNumberStatusFormModal: React.FC<Props> = ({
  isVisible,
  handleClose,
  sipNumberRecord,
}) => {
  const [form] = Form.useForm();
  const { currentUserId } = useContext(HeaderContext);

  const [state, setState] = useState<State>({
    isModalVisible: false,
    selectedSipStatus: "",
    poolsRegex: /.+/,
  });

  const {
    isModalVisible,
    poolsOptions,
    sipStatusesOptions,
    selectedSipStatus,
    poolsRegex,
  } = state;

  const handleCancel = () => {
    form.resetFields();
    setState((prevState) => ({
      ...prevState,
      submitError: "",
    }));
    handleClose();
  };

  const handleSubmit = () => {
    setState((prevState) => ({
      ...prevState,
      submitError: "",
    }));
    form
      .validateFields()
      .then((formFields) => {
        console.log("onOk formFields: %o", formFields);
        console.log("sipNumberRecord: %o", sipNumberRecord);
        const payload = {
          id: sipNumberRecord?.id || -1,
          phoneNumber: sipNumberRecord?.phoneNumber || -1,
          poolId: Number(getValueFromLabel(formFields.pools, poolsOptions)),
          sipStatusName: formFields.sipStatus,
          clientId: currentUserId,
          ...(selectedSipStatus === "Reserved"
            ? { comment: formFields.comment }
            : {}),
        };
        console.log("putSipNumbers() payload: %o", payload);
        putSipNumbers(payload).then((putResponse) => {
          console.log("submitResponse:%o", putResponse);
          if (putResponse?.error) {
            setState((prevState) => ({
              ...prevState,
              submitError: putResponse.error,
            }));
          } else {
            setState((prevState) => ({
              ...prevState,
              submitError: "",
            }));
            handleClose(true);
          }
        });
      })
      .catch((err) => {
        console.log("onOk err: %o", err);
      });
  };

  useEffect(() => {
    if (isVisible !== isModalVisible) {
      setState((prevState) => ({
        ...prevState,
        isModalVisible: isVisible,
        submitError: "",
      }));

      if (!isVisible) return;

      form.setFieldsValue({
        pools: sipNumberRecord?.pool?.name,
        sipStatus: sipNumberRecord?.sipStatus.name,
        comment: sipNumberRecord?.comment,
      });

      setState((prevState) => ({
        ...prevState,
        selectedSipStatus: sipNumberRecord?.sipStatus.name || "",
      }));

      if (!sipStatusesOptions) {
        getSipStatuses().then((response) => {
          if (response.data) {
            setState((prevState) => ({
              ...prevState,
              sipStatusesOptions: response.data,
            }));
          }
        });
      }
      if (!poolsOptions) {
        getPools().then((response) => {
          if (response.data) {
            setState((prevState) => ({
              ...prevState,
              poolsOptions: response.data,
              pools: 1,
            }));
          }
        });
      }

      console.log("sipNumberRecord: %o", sipNumberRecord);
    }
    // eslint-disable-next-line
  }, [isVisible, isModalVisible]);

  //Edit in Phone Number column
  return (
    <Modal
      title="Update Number Status"
      maskClosable={false}
      open={isModalVisible}
      onCancel={handleCancel}
      cancelText="Cancel"
      okText="Submit"
      onOk={handleSubmit}
      okButtonProps={{
        className: "bdo-red-button-modal",
      }}
      cancelButtonProps={{
        className: "bdo-grey-button-modal",
      }}
    >
      <DisplayFieldValues
        labelValues={[
          {
            label: "Phone Number",
            value: `${sipNumberRecord?.phoneNumberX}`,
          },
          {
            label: "Previous Status",
            value: sipNumberRecord?.sipStatus.name,
          },
        ]}
      />

      <Form form={form} labelCol={{ span: 6 }} wrapperCol={{ span: 16 }}>
        <Form.Item
          name="sipStatus"
          label="Current Status:"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          rules={[{ required: true, message: "This field is required" }]}
        >
          <Select
            placeholder="Select a status"
            onChange={(selectedLabel) => {
              setState((prevState) => ({
                ...prevState,
                selectedSipStatus: selectedLabel,
              }));
            }}
            allowClear
            options={[
              ...sipStatusChangeLogic[
                sipNumberRecord?.sipStatus.name || ""
              ]?.map((status) => ({ title: "", label: status, value: status })),
            ].sort(selectOptionsComparator)}
          ></Select>
        </Form.Item>

        <Form.Item
          name="pools"
          label="Current Pool:"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          rules={[{ required: true, message: "This field is required" }]}
        >
          <AutoComplete
            placeholder="Select a pool"
            onChange={(value: string) => {
              setState((prevState) => ({
                ...prevState,
                poolsRegex: createRegex(value),
              }));
            }}
            allowClear
            options={getLabelsAsValues(poolsOptions)}
            filterOption={(inputValue, option) => {
              return poolsRegex.test(option!.value);
            }}
          ></AutoComplete>
        </Form.Item>

        {selectedSipStatus === "Reserved" && (
          <Form.Item
            name="comment"
            label="Comment:"
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <Input />
          </Form.Item>
        )}
      </Form>

      {state.submitError && (
        <div className="text-red-500">{state.submitError}</div>
      )}
    </Modal>
  );
};

export default EditNumberStatusFormModal;
